import { Link, useLocation } from 'wouter';
import type { Bucket, Stats } from '../lib/api';
import { until } from '../lib/time';
import './rail.css';

/**
 * The queue, as five buckets.
 *
 * The order is the order they should be worked: what needs a person, what is
 * about to happen without one, what is only worth knowing, what refused, what
 * is done. v1 posted 138 Slack cards in seven days and nineteen in twenty
 * needed nothing — the signup approval sat in the same scroll as twenty-three
 * top-up reminders that had already sent themselves. Separating them IS the
 * feature.
 */
export const BUCKETS: { key: Bucket; label: string; tone: string }[] = [
  { key: 'needs_you', label: 'Needs you', tone: 'ink' },
  { key: 'veto', label: 'Veto running', tone: 'amber' },
  { key: 'quiet', label: 'Informational', tone: 'muted' },
  { key: 'failed', label: 'Refused at pre-flight', tone: 'rust' },
  { key: 'done_today', label: 'Done today', tone: 'muted' },
];
// The icon rail (fix-74) names a bucket by its icon and count; the label is on hover.
const BUCKET_ICONS: Partial<Record<Bucket, () => React.ReactElement>> = {
  needs_you: () => <InboxIcon />, veto: () => <TimerIcon />, quiet: () => <InfoIcon />,
  failed: () => <StopIcon />, done_today: () => <CheckIcon />,
};


/**
 * Where a card lives, INSIDE the bucket you are looking at.
 *
 * `/cards/:id` is the address every Slack post carries and it stays valid, but
 * it names no bucket — so arriving at one from a bucket DROPPED the bucket and
 * the queue silently reverted to "Needs you".
 *
 * That is what made "Refused at pre-flight" look dead: the rail navigated to
 * /b/failed, the list loaded, Desk's auto-select replaced the URL with
 * /cards/904, the bucket fell back to its default, and the middle pane went
 * back to Needs you — while the right-hand pane still showed the card that had
 * been picked out of the failed bucket. Because the auto-select uses `replace`
 * there was no history entry either, so nothing appeared to happen at all.
 */
export const cardHref = (bucket: Bucket, id: number | string) =>
  (bucket === 'needs_you' ? `/cards/${id}` : `/b/${bucket}/cards/${id}`);

/**
 * `compact` (fix-74): with a card open on a desktop the rail is icons only, so the card gets the width. The label of
 * each item is its tooltip and its accessible name.
 */
export function Rail({ stats, bucket, now, compact = false, operator = false }: {
  stats: Stats | null; bucket: Bucket; now: number; compact?: boolean; operator?: boolean;
}) {
  return (
    <nav className={`rail ${compact ? 'rail-compact' : ''}`} aria-label="Card buckets">
      <div className="rail-head mono">Queue</div>
      {BUCKETS.map((b) => (
        <Link
          key={b.key}
          href={b.key === 'needs_you' ? '/' : `/b/${b.key}`}
          className={`rail-item ${bucket === b.key ? 'is-current' : ''}`}
          aria-current={bucket === b.key ? 'page' : undefined}
          aria-label={compact ? b.label : undefined}
          data-tip={compact ? b.label : undefined}
        >
          {compact && BUCKET_ICONS[b.key]?.()}
          <span className="rail-label">{b.label}</span>
          <span className={`rail-count mono tone-${b.tone}`}>
            {stats ? stats.cards[b.key] ?? 0 : '·'}
            {b.key === 'veto' && stats?.veto_next_deadline
              // The soonest deadline, because that is the one that is about to
              // happen. A count alone does not tell you whether you have an
              // hour or four minutes.
              ? ` · ${until(stats.veto_next_deadline, now)}`
              : ''}
          </span>
        </Link>
      ))}

      <div className="rail-rule" />
      <div className="rail-head mono">Boards</div>
      <RailLink href="/lifeline" label="Lifeline" icon={<PulseIcon />} compact={compact} />
      <RailLink href="/classes" label="Classes & spend" icon={<BarsIcon />} compact={compact} />
      <RailLink href="/done" label="Done today" icon={<ClockIcon />} compact={compact} />
      <RailLink href="/health" label="Health" icon={<ShieldIcon />} compact={compact} />
      <RailLink href="/finance" label="Finance" icon={<CoinIcon />} compact={compact} />
      <RailLink href="/knowledge" label="Knowledge" icon={<BookIcon />} compact={compact} />
      {/* notices-1 (BCST-26): the operator's alone, as its routes are. */}
      {operator && <RailLink href="/notices" label="Notices" icon={<MegaphoneIcon />} compact={compact} />}
    </nav>
  );
}

function RailLink({ href, label, icon, compact }: { href: string; label: string; icon: React.ReactNode; compact: boolean }) {
  const [location] = useLocation();
  return (
    <Link href={href} className={`rail-item rail-board ${location === href ? 'is-current' : ''}`}
      aria-label={compact ? label : undefined} data-tip={compact ? label : undefined}>
      {icon}
      <span className="rail-label">{label}</span>
    </Link>
  );
}

const svg = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, 'aria-hidden': true };
const PulseIcon = () => <svg {...svg}><path d="M3 12h4l3-7 4 14 3-7h4" /></svg>;
const BarsIcon = () => <svg {...svg}><path d="M4 20V10M10 20V4M16 20v-8M22 20H2" /></svg>;
const ClockIcon = () => <svg {...svg}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
const CoinIcon = () => <svg {...svg}><circle cx="12" cy="12" r="9" /><path d="M15 9.5c-.6-1-1.7-1.5-3-1.5-1.7 0-3 .9-3 2s1.3 1.7 3 2 3 .9 3 2-1.3 2-3 2c-1.3 0-2.4-.5-3-1.5M12 6v12" /></svg>;
const BookIcon = () => <svg {...svg}><path d="M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2zM4 5v16M8 7h7M8 11h7" /></svg>;
const ShieldIcon = () => <svg {...svg}><path d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6z" /></svg>;
const InboxIcon = () => <svg {...svg}><path d="M3 13l3-8h12l3 8v6H3zM3 13h5l1 3h6l1-3h5" /></svg>;
const TimerIcon = () => <svg {...svg}><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2M9 2h6" /></svg>;
const InfoIcon = () => <svg {...svg}><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7.5v.5" /></svg>;
const StopIcon = () => <svg {...svg}><circle cx="12" cy="12" r="9" /><path d="M6 6l12 12" /></svg>;
const MegaphoneIcon = () => <svg {...svg}><path d="M3 10v4h4l6 4V6L7 10zM16 9a4 4 0 010 6M19 6a8 8 0 010 12" /></svg>;
const CheckIcon = () => <svg {...svg}><path d="M4 12l5 5L20 6" /></svg>;
